import { BottomSheetTextInput } from '@gorhom/bottom-sheet';
import { type Ref, useRef, useState } from 'react';
import {
  type BlurEvent,
  type FocusEvent,
  TextInput as RNTextInput,
  type TextInputProps as RNTextInputProps,
  type StyleProp,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { StyleSheet, withUnistyles } from 'react-native-unistyles';
import useAutoFocusAfterTransition from '@/hooks/useAutoFocusAfterTransition';

type TextInputVariant = 'outline' | 'plain' | 'primary' | 'underlined';
type TextInputSize = 'sm' | 'md' | 'lg' | 'xl';

export interface TextInputProps extends Omit<RNTextInputProps, 'style'> {
  containerStyle?: StyleProp<ViewStyle>;
  disabled?: boolean;
  flex?: number | boolean;
  isBottomSheet?: boolean;
  isInvalid?: boolean;
  leftElement?: React.ReactNode;
  maxWidth?: ViewStyle['maxWidth'];
  minWidth?: ViewStyle['minWidth'];
  ref?: Ref<RNTextInput>;
  rightElement?: React.ReactNode;
  size?: TextInputSize;
  standalone?: boolean;
  style?: StyleProp<TextStyle>;
  variant?: TextInputVariant;
  width?: ViewStyle['width'];
}

const ThemedTextInput = withUnistyles(RNTextInput, (theme) => ({
  placeholderTextColor: theme.colors.typographySecondary,
}));

const ThemedBottomSheetTextInput = withUnistyles(
  BottomSheetTextInput,
  (theme) => ({
    placeholderTextColor: theme.colors.typographySecondary,
  }),
);

export default function TextInput({
  autoFocus,
  containerStyle,
  disabled,
  flex,
  isBottomSheet = false,
  isInvalid,
  leftElement,
  maxWidth,
  minWidth,
  onBlur: onBlurProp,
  onFocus: onFocusProp,
  ref,
  rightElement,
  size = 'md',
  standalone,
  style: extraStyles,
  variant = 'outline',
  width,
  ...props
}: TextInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<RNTextInput | null>(null);
  useAutoFocusAfterTransition(inputRef, autoFocus);

  styles.useVariants({
    disabled,
    isFocused,
    isInvalid,
    size,
    variant,
  });

  const setInputRef = (node: RNTextInput | null | undefined) => {
    const nextNode = node ?? null;
    inputRef.current = nextNode;
    assignRef(ref, nextNode);
  };

  const onFocus = (event: FocusEvent) => {
    setIsFocused(true);
    onFocusProp?.(event);
  };

  const onBlur = (event: BlurEvent) => {
    setIsFocused(false);
    onBlurProp?.(event);
  };

  const sharedProps = {
    ...props,
    autoFocus: process.env.EXPO_OS === 'web' ? autoFocus : false,
    editable: disabled ? false : props.editable,
    onBlur,
    onFocus,
    ref: setInputRef,
    style: [
      styles.input({
        hasLeftElement: Boolean(leftElement),
        hasRightElement: Boolean(rightElement),
        multiline: Boolean(props.multiline),
      }),
      extraStyles,
    ],
  };

  if (standalone) {
    const standaloneProps = {
      ...props,
      autoFocus: process.env.EXPO_OS === 'web' ? autoFocus : false,
      editable: disabled ? false : props.editable,
      onBlur,
      onFocus,
      ref: setInputRef,
      style: [
        styles.standaloneInput({
          isFocused,
          multiline: Boolean(props.multiline),
        }),
        extraStyles,
      ],
    };

    if (isBottomSheet && process.env.EXPO_OS === 'native') {
      return <ThemedBottomSheetTextInput {...standaloneProps} />;
    }

    return <ThemedTextInput {...standaloneProps} />;
  }

  return (
    <View
      style={[
        styles.root({
          flex,
          maxWidth,
          minWidth,
          multiline: Boolean(props.multiline),
          width,
        }),
        containerStyle,
      ]}
    >
      {leftElement ? <View style={styles.accessory}>{leftElement}</View> : null}
      {isBottomSheet && process.env.EXPO_OS === 'native' ? (
        <ThemedBottomSheetTextInput {...sharedProps} />
      ) : (
        <ThemedTextInput {...sharedProps} />
      )}
      {rightElement ? (
        <View style={styles.accessory}>{rightElement}</View>
      ) : null}
    </View>
  );
}

function assignRef<TValue>(ref: Ref<TValue> | undefined, value: TValue | null) {
  if (!ref) {
    return;
  }

  if (typeof ref === 'function') {
    ref(value);
    return;
  }

  ref.current = value;
}

const styles = StyleSheet.create((theme) => ({
  accessory: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  input: ({
    hasLeftElement,
    hasRightElement,
    multiline,
  }: {
    hasLeftElement: boolean;
    hasRightElement: boolean;
    multiline: boolean;
  }) => ({
    backgroundColor: 'transparent',
    color: theme.colors.typography,
    flex: 1,
    fontSize: theme.fontSize.md,
    height: multiline ? '100%' : undefined,
    minWidth: 0,
    paddingLeft: hasLeftElement ? 0 : theme.space('md'),
    paddingRight: hasRightElement ? 0 : theme.space('md'),
    paddingVertical: multiline ? theme.space('md') : 0,
    textAlignVertical: multiline ? 'top' : 'center',
    _web: {
      outlineStyle: 'none',
    },
    variants: {
      size: {
        lg: {
          fontSize: theme.fontSize.lg,
          minHeight: theme.size(12),
        },
        md: {
          fontSize: theme.fontSize.md,
          minHeight: theme.size(10),
        },
        sm: {
          fontSize: theme.fontSize.sm,
          minHeight: theme.size(8),
        },
        xl: {
          fontSize: theme.fontSize.xl,
          minHeight: theme.size(14),
        },
      },
    },
  }),
  root: ({
    flex,
    maxWidth,
    minWidth,
    width,
    multiline,
  }: Pick<TextInputProps, 'flex' | 'maxWidth' | 'minWidth' | 'width'> & {
    multiline: boolean;
  }) => ({
    alignItems: multiline ? 'stretch' : 'center',
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.borderMuted,
    borderRadius: theme.borderRadius.xl,
    borderWidth: StyleSheet.hairlineWidth,
    flex: typeof flex === 'number' ? flex : flex ? 1 : undefined,
    flexDirection: 'row',
    maxWidth,
    minWidth,
    overflow: 'hidden',
    width,
    variants: {
      disabled: {
        true: {
          opacity: 0.5,
        },
      },
      isFocused: {
        true: {
          borderColor: theme.colors.primary,
        },
      },
      isInvalid: {
        true: {
          borderColor: theme.colors.error,
        },
      },
      size: {
        lg: {
          gap: theme.space('sm'),
          paddingHorizontal: theme.space('md'),
        },
        md: {
          gap: theme.space('sm'),
          paddingHorizontal: theme.space('md'),
        },
        sm: {
          gap: theme.space('xs'),
          paddingHorizontal: theme.space('sm'),
        },
        xl: {
          gap: theme.space('sm'),
          paddingHorizontal: theme.space('md'),
        },
      },
      variant: {
        outline: {},
        plain: {
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          borderWidth: 0,
        },
        primary: {},
        underlined: {
          backgroundColor: 'transparent',
          borderLeftWidth: 0,
          borderRadius: theme.borderRadius.none,
          borderRightWidth: 0,
          borderTopWidth: 0,
        },
      },
    },
    _web: {
      _focusWithin: {
        borderColor: theme.colors.primary,
      },
    },
  }),
  standaloneInput: ({
    isFocused,
    multiline,
  }: {
    isFocused: boolean;
    multiline: boolean;
  }) => ({
    borderColor: isFocused ? theme.colors.primary : theme.colors.borderMuted,
    borderRadius: theme.borderRadius.xl,
    borderWidth: theme.size(0.5),
    color: theme.colors.typography,
    fontSize: theme.fontSize.lg,
    minHeight: multiline ? undefined : theme.size(10),
    paddingHorizontal: theme.size(3),
    paddingVertical: multiline ? theme.space('md') : 0,
    textAlignVertical: multiline ? 'top' : 'center',
    _web: {
      outline: isFocused
        ? `${theme.size(0.25)}px solid ${theme.colors.primary}`
        : 'none',
      outlineOffset: 0,
    },
  }),
}));
